import axios from 'axios';
import {
  body,
  setHtmlScrollable,
  setHtmlUnscrollable
} from '@jspartials/general-utils';
import {
  listenDOMEvent,
  createElement,
  appendElementContent
} from '@jspartials/dom-api-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement
} from '@jspartials/class-list-utils';
import Recommendations from '@jsclasses/recommendations';

// Classes
const className = {
  notification: 'js-notification'
};

// Selectors
const selector = {
  wrapper: '.js-pdp-add-to-cart-notification-wrapper',
  notification: `.${className.notification}`,
  button: {
    close: '.js-close-notification'
  },
  recommendations: {
    afterAddToCartInPdp: '.js-pdp__recommendations-after-add-to-cart'
  },
  currentProduct: {
    wrapper: '.js-current-product-added-wrapper',
    details: '.js-current-product-added-details'
  }
};

// Variables
const timeToInitAnimation = 100;
const timeToCloseNotification = 5000;

/**
 * Destroy all notifications
 * @returns {void}
 */
const destroyNotifications = () => {
  const notifications = document.querySelectorAll(selector.notification);

  notifications.forEach((notification) => {
    notification.remove();
  });
};

/**
 * Paint notifications. Append notifications to body
 * @param {NodeListOf<Element>} notifications - Notifications
 * @returns {void}
 */
const paintNotifications = (notifications) => {
  notifications.forEach((notification) => {
    appendElementContent(body, notification);
  });
};

/**
 * Show notifications
 * @param {NodeListOf<Element>} notifications - Notifications
 * @returns {void}
 */
const showNotifications = (notifications) => {
  setTimeout(() => {
    notifications.forEach((notification) => {
      addClassToElement(notification, classes.is.visible);
    });
  }, timeToInitAnimation);
};

/**
 * Hide notifications
 * @param {NodeListOf<Element>} notifications - Notifications
 * @returns {void}
 */
const hideNotifications = (notifications) => {
  setTimeout(() => {
    notifications.forEach((notification) => {
      if (!notification.classList.contains(classes.is.locked)) {
        removeClassToElement(notification, classes.is.visible);
      }
    });
  }, timeToCloseNotification);
};

/**
 * Listen close notification
 * @param {Element} notification - Notification
 * @returns {void}
 */
const listenCloseNotification = (notification) => {
  const closeButtons =
    notification.querySelectorAll(selector.button.close) || [];

  closeButtons.forEach((closeButton) => {
    closeButton.addEventListener('click', () => {
      removeClassToElement(notification, classes.is.visible);
      setHtmlScrollable();
    });
  });
};

const initRecommendationsAfterAddToCartInPdp = () => {
  const recommendationsAfterAddToCartInPdp =
    document.querySelector(selector.recommendations.afterAddToCartInPdp) ||
    null;

  if (recommendationsAfterAddToCartInPdp) {
    new Recommendations({
      wrapper: recommendationsAfterAddToCartInPdp,
      showPrice: true,
      maxVisibleItems: 2
    });
  }
};

/**
 * Notify after added to cart successfully in product tile
 * @param {string} productId - Product ID
 * @returns {void}
 */
const notifyAfterAddedToCartSuccessfullyInProductTile = (
  productId,
  errorMessage = ''
) => {
  const formData = new FormData();
  formData.append('productId', productId);
  formData.append('errorMessage', errorMessage);

  // Destroy all notifications
  destroyNotifications();

  axios
    .post(
      globalVariables.endpoint
        .getNotificationsAfterAddedToCartSuccessfullyInProductTile,
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    .then((response) => {
      const [htmlMobileNotification, htmlDesktopNotification] =
        response.data.html;
      const mobileNotification = createElement({
        tag: 'div',
        content: htmlMobileNotification,
        classes: [className.notification]
      });
      const desktopNotification = createElement({
        tag: 'div',
        content: htmlDesktopNotification,
        classes: [className.notification]
      });
      const mobileNotificationFirstElementChild =
        mobileNotification.firstElementChild;
      const desktopNotificationFirstElementChild =
        desktopNotification.firstElementChild;

      // Paint notifications
      paintNotifications([mobileNotification, desktopNotification]);

      // Listen close notification
      listenCloseNotification(desktopNotificationFirstElementChild);

      // Show notification
      showNotifications([
        mobileNotificationFirstElementChild,
        desktopNotificationFirstElementChild
      ]);

      // Hide notification
      hideNotifications([
        mobileNotificationFirstElementChild,
        desktopNotificationFirstElementChild
      ]);
    })
    .catch((error) => {
      console.error(error);
    });
};

/**
 * Notify after added to cart successfully in product details page
 * @param {string} productId - Product ID
 * @param {Array} items - Items (optional)
 * @returns {void}
 */
const notifyAfterAddedToCartSuccessfullyInProductDetails = (
  productId,
  items = [],
  quantity = 1
) => {
  const formData = new FormData();
  formData.append('productId', productId);
  formData.append('items', JSON.stringify(items));
  formData.append('quantity', quantity);

  // Destroy all notifications
  destroyNotifications();

  axios
    .post(
      globalVariables.endpoint
        .getNotificationsAfterAddedToCartSuccessfullyInProductDetails,
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    .then((response) => {
      const [htmlMobileNotification] = response.data.html;
      const mobileNotification = createElement({
        tag: 'div',
        content: htmlMobileNotification,
        classes: [className.notification]
      });
      const mobileNotificationFirstElementChild =
        mobileNotification.firstElementChild;

      // Paint notifications
      paintNotifications([mobileNotification]);

      // Listen close notification
      listenCloseNotification(mobileNotificationFirstElementChild);

      // Init recommendations
      initRecommendationsAfterAddToCartInPdp();

      // Check user interaction
      checkUserInteraction(mobileNotificationFirstElementChild);

      // Show notification
      showNotifications([mobileNotificationFirstElementChild]);

      // Hide notification
      hideNotifications([mobileNotificationFirstElementChild]);

      // Set unscrollable html
      setHtmlUnscrollable();
    })
    .catch((error) => {
      console.error(error);
    });
};

const checkUserInteraction = (notificationElement) => {
  listenDOMEvent(notificationElement, 'click', () => {
    addClassToElement(notificationElement, classes.is.locked);
  });
  listenDOMEvent(notificationElement, 'mouseover', () => {
    addClassToElement(notificationElement, classes.is.locked);
  });
  listenDOMEvent(notificationElement, 'touchstart', () => {
    addClassToElement(notificationElement, classes.is.locked);
  });
};

const updateNotificationAfterAddedToCartSuccessfullyInProductDetails = (
  productId
) => {
  const formData = new FormData();
  formData.append('productId', productId);

  axios
    .post(
      globalVariables.endpoint
        .updateNotificationAfterAddedToCartSuccessfullyInProductDetails,
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )
    .then((response) => {
      const [html] = response.data.html;

      replaceAddedProduct(html);
    })
    .catch((error) => {
      console.error(error);
    });
};

const getCurrentProductAddedDetailsElement = () => {
  return document.querySelector(selector.currentProduct.details) || null;
};

const replaceAddedProduct = (html) => {
  const currentProductAddedWrapper =
    document.querySelector(selector.currentProduct.wrapper) || null;
  let currentProductAddedDetails = getCurrentProductAddedDetailsElement();
  const newCurrentProductAddedDetails = createElement({
    tag: 'div',
    content: html,
    classes: [
      'tw-absolute',
      'tw-top-0',
      'tw-left-0',
      'tw-flex',
      'tw-justify-between',
      'tw-w-full',
      'tw-h-full',
      'tw-gap-4',
      'tw-duration-500',
      'tw-ease-in-out',
      'tw-translate-x-full',
      'js-current-product-added-details',
      '[&.is-animated-in]:tw-translate-x-0',
      '[&.is-animated-in.is-animated-out]:-tw-translate-x-full'
    ]
  });

  if (currentProductAddedWrapper && currentProductAddedDetails) {
    removeClassToElement(
      currentProductAddedDetails,
      selector.currentProduct.details.replace('.', '')
    );
    addClassToElement(currentProductAddedDetails, classes.is.animatedOut);
    currentProductAddedWrapper.appendChild(newCurrentProductAddedDetails);
    currentProductAddedDetails = getCurrentProductAddedDetailsElement();
    setTimeout(() => {
      addClassToElement(currentProductAddedDetails, classes.is.animatedIn);
    }, 150);
  }
};

export {
  notifyAfterAddedToCartSuccessfullyInProductTile,
  notifyAfterAddedToCartSuccessfullyInProductDetails,
  updateNotificationAfterAddedToCartSuccessfullyInProductDetails
};
